/**
 * useUndoStack Hook (Story 5.6, Task 6)
 *
 * Lightweight undo/redo state management with immutability guarantee.
 * Max history: 50 states (configurable) to prevent memory bloat.
 *
 * CRITICAL: This hook relies on callers providing NEW state objects (not mutations).
 * All state updates must use spread operators for immutability.
 * In dev mode, Object.freeze() detects accidental mutations.
 */

import { useState, useCallback, useRef } from "react";

export interface UndoStack<T> {
  state: T;
  setState: (next: T | ((prev: T) => T)) => void;
  /** Updates state without pushing to undo/redo stacks (for computed changes like auto-resize) */
  setStateSilent: (next: T | ((prev: T) => T)) => void;
  undo: () => void;
  redo: () => void;
  reset: (newInitial: T) => void;
  canUndo: boolean;
  canRedo: boolean;
}

export function useUndoStack<T>(initial: T, maxHistory = 50): UndoStack<T> {
  const [state, setStateInternal] = useState(initial);
  const undoStack = useRef<T[]>([]);
  const redoStack = useRef<T[]>([]);

  // Force re-render when undo/redo stack changes (for canUndo/canRedo)
  const [, forceUpdate] = useState(0);

  const setState = useCallback(
    (next: T | ((prev: T) => T)) => {
      setStateInternal((prev) => {
        const nextState = typeof next === "function" ? (next as (p: T) => T)(prev) : next;
        undoStack.current = [...undoStack.current.slice(-(maxHistory - 1)), prev];
        redoStack.current = [];

        // DEV MODE: Freeze pushed state to catch accidental mutations
        if (import.meta.env.DEV) {
          try {
            Object.freeze(prev);
          } catch {
            // Already frozen or non-freezable — ignore
          }
        }

        return nextState;
      });
      forceUpdate((n) => n + 1);
    },
    [maxHistory]
  );

  const setStateSilent = useCallback(
    (next: T | ((prev: T) => T)) => {
      setStateInternal((prev) =>
        typeof next === "function" ? (next as (p: T) => T)(prev) : next
      );
    },
    []
  );

  const undo = useCallback(() => {
    if (undoStack.current.length === 0) return;
    setStateInternal((prev) => {
      const previous = undoStack.current.pop()!;
      redoStack.current.push(prev);
      return previous;
    });
    forceUpdate((n) => n + 1);
  }, []);

  const redo = useCallback(() => {
    if (redoStack.current.length === 0) return;
    setStateInternal((prev) => {
      const next = redoStack.current.pop()!;
      undoStack.current.push(prev);
      return next;
    });
    forceUpdate((n) => n + 1);
  }, []);

  const reset = useCallback((newInitial: T) => {
    undoStack.current = [];
    redoStack.current = [];
    setStateInternal(newInitial);
    forceUpdate((n) => n + 1);
  }, []);

  return {
    state,
    setState,
    setStateSilent,
    undo,
    redo,
    reset,
    canUndo: undoStack.current.length > 0,
    canRedo: redoStack.current.length > 0,
  };
}
